import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Autocomplete,
  Checkbox,
  Chip,
  Collapse,
  Grid,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  castArray,
  filter,
  find,
  includes,
  isBoolean,
  isEmpty,
  join,
  map,
  some,
  startCase,
  toString,
  uniq,
} from 'es-toolkit/compat';
import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { ClassToAnnotateListItem } from 'components/StudyDashboard/AnnotationAssignment/ClassesToAnnotateList';
import { TodoOption } from 'interfaces/annotation';
import { IFormValues } from './types';

interface Props {
  targetClass: string;
  classConfig?: {
    id?: string;
    color?: string;
    dataItems?: string[];
    ignore?: boolean;
    hollowShape?: boolean;
    isPoint?: boolean;
  };
  formMethods: UseFormReturn<Partial<IFormValues>, any>;
  availableOptions: TodoOption[];
  availableOptionNames: string[];
}

const ClassConfigDetails: React.FC<
  React.PropsWithChildren<{
    fieldName: string;
    fieldValue: string;
  }>
> = ({ fieldName, fieldValue }) => {
  if (isEmpty(fieldValue)) return <></>;

  return (
    <Grid item container spacing={1} alignItems="center">
      <Grid item xs={4}>
        <Typography variant="body2">{fieldName}:</Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2"> {fieldValue}</Typography>
      </Grid>
    </Grid>
  );
};

const basePath = 'trainingClassToAnnotationOptions' as const;

export const TargetClassRow: React.FC<Props> = ({
  classConfig,
  targetClass,
  formMethods,
  availableOptions,
  availableOptionNames: allAvailableOptionNames,
}) => {
  const [open, setOpen] = React.useState(false);

  const trainingClassToAnnotationOptions = formMethods.watch('trainingClassToAnnotationOptions') || {};
  const disabledTrainingTargets = formMethods.watch('disabledTrainingTargets') || [];

  const availableOptionNames = useMemo(
    () =>
      filter(
        allAvailableOptionNames,
        (optionName) =>
          !some(
            trainingClassToAnnotationOptions,
            (assignments, className) =>
              className !== targetClass &&
              !includes(disabledTrainingTargets, className) &&
              includes(assignments, optionName)
          )
      ),
    [allAvailableOptionNames, trainingClassToAnnotationOptions, targetClass, disabledTrainingTargets]
  );
  const isDisabled = includes(disabledTrainingTargets, targetClass);

  const fieldPath = `${basePath}.${targetClass}` as const;
  const value = formMethods.watch(fieldPath) || [];
  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Checkbox
            checked={!isDisabled}
            onChange={(event) =>
              formMethods.setValue(
                'disabledTrainingTargets',
                event.target.checked
                  ? // Remove the target class if the checkbox is checked
                    filter(disabledTrainingTargets, (id) => id !== targetClass)
                  : // Add the target class if the checkbox is unchecked
                    uniq([...disabledTrainingTargets, targetClass])
              )
            }
          />
        </TableCell>
        <TableCell>{targetClass}</TableCell>
        <TableCell>
          <Autocomplete<string, true, false, false>
            multiple
            disabled={isDisabled}
            disableCloseOnSelect
            value={!isDisabled ? castArray(value) : []}
            options={availableOptionNames}
            onChange={(event, newValue) => {
              formMethods.setValue(fieldPath, newValue, {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
              });
            }}
            renderInput={(params) => (
              <TextField {...params} label={`Select annotation classes to map to ${targetClass}`} size="small" />
            )}
            renderOption={(props, optionName, { selected }) => {
              const option = find(availableOptions, { name: optionName });
              if (!option) {
                return (
                  <li key={optionName} {...props}>
                    {optionName} (option details not found)
                  </li>
                );
              }
              return (
                <li key={optionName} {...props} style={{ ...props.style, padding: 0 }}>
                  <ClassToAnnotateListItem isSelectable key={optionName} item={option} isSelected={selected} />
                </li>
              );
            }}
            renderTags={(selectedOptions, getTagProps) =>
              map(selectedOptions, (optionName, index) => {
                const option = find(availableOptions, { name: optionName });
                const { key: tagKey, ...tagProps } = getTagProps({ index });
                if (!option) {
                  return <Chip key={tagKey} {...tagProps} label={`${optionName} (option details not found)`} />;
                }
                return <ClassToAnnotateListItem key={tagKey} {...tagProps} item={option} />;
              })
            }
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid container direction="column">
              <ClassConfigDetails fieldName="Id" fieldValue={classConfig?.id ?? '---'} />
              <ClassConfigDetails fieldName="Color" fieldValue={classConfig?.color ?? '---'} />
              <ClassConfigDetails fieldName="Data Items" fieldValue={join(classConfig?.dataItems, ', ') ?? '---'} />
              <ClassConfigDetails
                fieldName="Ignore"
                fieldValue={isBoolean(classConfig?.ignore) ? startCase(toString(classConfig?.ignore)) : '---'}
              />
              <ClassConfigDetails
                fieldName="Hollow Shape"
                fieldValue={isBoolean(classConfig?.hollowShape) ? startCase(toString(classConfig?.hollowShape)) : '---'}
              />
              <ClassConfigDetails
                fieldName="Is Point"
                fieldValue={isBoolean(classConfig?.isPoint) ? startCase(toString(classConfig?.isPoint)) : '---'}
              />
            </Grid>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
